import { useCallback, useEffect, useState } from "react";
import { Search, Download, LogOut, Loader2, Users, PhoneCall, BadgeCheck, Trophy, Inbox, Mail, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, authHeaders, formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";

const STATUSES = ["New", "Contacted", "Qualified", "Converted", "Closed"];
const STATUS_COLORS = {
  New: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Qualified: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  Converted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("and_admin_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="admin-login-screen">
      <div className="w-full max-w-sm glass-card rounded-3xl p-8">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <h1 className="font-display text-xl font-bold text-white text-center">Admin Dashboard</h1>
        <p className="text-xs text-slate-400 text-center mt-1 mb-6">Sign in to view and manage leads</p>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">Email</label>
            <input
              data-testid="admin-login-email-input"
              type="email"
              className="field-input"
              placeholder="admin@adneurone.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">Password</label>
            <input
              data-testid="admin-login-password-input"
              type="password"
              className="field-input"
              placeholder="••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && (
            <p className="text-xs text-red-400" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 gradient-bg text-white font-semibold text-sm px-6 py-3 rounded-xl hover:brightness-110 transition-[filter] duration-200 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");

  const logout = useCallback(() => {
    localStorage.removeItem("and_admin_token");
    setUser(null);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes, recipRes] = await Promise.all([
        api.get("/admin/leads", { headers: authHeaders(), params: { search, status } }),
        api.get("/admin/stats", { headers: authHeaders() }),
        api.get("/admin/recipients", { headers: authHeaders() }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setRecipients(recipRes.data.emails || []);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [search, status, logout]);

  useEffect(() => {
    const token = localStorage.getItem("and_admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me", { headers: authHeaders() })
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem("and_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/admin/leads/${leadId}`, { status: newStatus }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
      toast.success(`Marked as ${newStatus}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addRecipient = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      const res = await api.post("/admin/recipients", { email: newEmail.trim() }, { headers: authHeaders() });
      setRecipients(res.data.emails);
      setNewEmail("");
      toast.success("Recipient added");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeRecipient = async (email) => {
    try {
      const res = await api.delete(`/admin/recipients/${encodeURIComponent(email)}`, { headers: authHeaders() });
      setRecipients(res.data.emails);
      toast.success("Recipient removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export/csv", {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ad-neurone-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (checking)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-blue-400" />
      </div>
    );

  if (!user) return <LoginScreen onLogin={setUser} />;

  const statCards = [
    { label: "Total Leads", value: stats?.total ?? 0, icon: Users },
    { label: "New", value: stats?.by_status?.New ?? 0, icon: Inbox },
    { label: "Contacted", value: stats?.by_status?.Contacted ?? 0, icon: PhoneCall },
    { label: "Qualified", value: stats?.by_status?.Qualified ?? 0, icon: BadgeCheck },
    { label: "Converted", value: stats?.by_status?.Converted ?? 0, icon: Trophy },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050A1E] px-4 sm:px-8 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <Logo />
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-300 border border-white/15 rounded-full px-4 py-2 hover:text-white hover:border-red-400/50 transition-colors duration-200"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">Leads Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">Every enquiry from the website, in one place.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((s) => (
            <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="glass-card rounded-2xl p-5">
              <s.icon size={18} className="text-cyan-400 mb-2" />
              <p className="font-display text-2xl font-extrabold text-white">{s.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="glass-card rounded-2xl p-5 sm:p-6" data-testid="recipients-card">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold text-white flex items-center gap-2">
                <Mail size={17} className="text-cyan-400" /> Lead Email Notifications
              </h2>
              <p className="text-xs text-slate-400 mt-1">
                Every new lead is emailed instantly to all of these addresses.
              </p>
            </div>
            <form onSubmit={addRecipient} className="flex gap-2 w-full lg:w-auto" data-testid="add-recipient-form">
              <input
                data-testid="recipient-email-input"
                type="email"
                required
                className="field-input flex-1 lg:w-72"
                placeholder="teammate@adneuronedigital.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              <button
                data-testid="add-recipient-button"
                type="submit"
                className="inline-flex items-center gap-1.5 gradient-bg text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:brightness-110 transition-[filter] duration-200"
              >
                <Plus size={15} /> Add
              </button>
            </form>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {recipients.map((r) => (
              <span
                key={r}
                data-testid={`recipient-chip-${r}`}
                className="inline-flex items-center gap-2 text-xs font-medium text-cyan-200 bg-cyan-500/10 border border-cyan-400/25 rounded-full px-3.5 py-1.5"
              >
                {r}
                <button
                  data-testid={`remove-recipient-${r}`}
                  onClick={() => removeRecipient(r)}
                  aria-label={`Remove ${r}`}
                  className="text-slate-400 hover:text-red-400 transition-colors duration-150"
                >
                  <X size={13} />
                </button>
              </span>
            ))}
            {recipients.length === 0 && (
              <p className="text-xs text-slate-500" data-testid="no-recipients-message">
                No recipients yet — add at least one email to get lead alerts.
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="admin-search-input"
              className="field-input !pl-10"
              placeholder="Search name, email, phone or business..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="admin-status-filter"
            className="field-input sm:w-48"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            data-testid="admin-export-csv-button"
            onClick={exportCsv}
            className="inline-flex items-center justify-center gap-2 gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:brightness-110 transition-[filter] duration-200"
          >
            <Download size={15} /> Export CSV
          </button>
        </div>
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-4 font-semibold">Lead</th>
                  <th className="px-5 py-4 font-semibold">Business</th>
                  <th className="px-5 py-4 font-semibold">Service</th>
                  <th className="px-5 py-4 font-semibold">Budget</th>
                  <th className="px-5 py-4 font-semibold">Source</th>
                  <th className="px-5 py-4 font-semibold">Date</th>
                  <th className="px-5 py-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-150">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-white">{lead.full_name}</p>
                      <p className="text-xs text-slate-400">{lead.email}</p>
                      <p className="text-xs text-slate-400">{lead.phone}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-300">{lead.business_name}</td>
                    <td className="px-5 py-4 text-slate-300 max-w-[180px]">{lead.service}</td>
                    <td className="px-5 py-4 text-slate-300 whitespace-nowrap">{lead.budget}</td>
                    <td className="px-5 py-4">
                      <span className="text-xs font-mono2 text-cyan-300">{lead.utm_source || "direct"}</span>
                      {lead.utm_campaign && (
                        <p className="text-[10px] text-slate-500 font-mono2">{lead.utm_campaign}</p>
                      )}
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-400 whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`lead-status-select-${lead.id}`}
                        value={lead.status}
                        onChange={(e) => updateStatus(lead.id, e.target.value)}
                        className={`text-xs font-semibold rounded-full px-3 py-1.5 border bg-transparent outline-none cursor-pointer ${STATUS_COLORS[lead.status] || STATUS_COLORS.New}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-[#0B132B] text-white">
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center text-slate-500 text-sm" data-testid="no-leads-message">
                      No leads found. They'll appear here the moment someone submits the form.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
